import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, UserPlus, Shield, X, AlertTriangle, Users } from 'lucide-react';
import CreateRoleForm from './CreateRoleForm';
import RoleList from './RoleList';
import { db, collection, getDocs, writeBatch, doc } from '../../firebase';
import styles from '../CSS/RoleManagement.module.css';

const RoleManagement = ({ 
  roles, 
  availablePermissions, 
  onUpdate, 
  showFeedback, 
  currentUser, 
  users,
  organizationalUnits = []
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const handleCreateClick = () => {
    setShowCreateModal(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    onUpdate();
    showFeedback("success", "Rol creado exitosamente");
    document.body.style.overflow = 'unset';
  };

  const handleCreateCancel = () => {
    setShowCreateModal(false);
    document.body.style.overflow = 'unset';
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    document.body.style.overflow = 'hidden';
  };

  const handleEditSuccess = () => {
    setEditingRole(null);
    onUpdate();
    showFeedback("success", "Rol actualizado exitosamente");
    document.body.style.overflow = 'unset';
  };

  const handleEditCancel = () => {
    setEditingRole(null);
    document.body.style.overflow = 'unset';
  };

  const canDeleteRole = (roleId) => {
    if (roleId === 'user') {
      return {
        canDelete: false,
        reason: 'El rol "usuario" es el rol por defecto del sistema y no puede ser eliminado'
      };
    }

    if (roleId === 'admin') {
      return {
        canDelete: false,
        reason: 'El rol "administrador" es un rol del sistema y no puede ser eliminado'
      };
    }

    if (currentUser?.role === roleId) {
      return {
        canDelete: false,
        reason: 'No puedes eliminar tu propio rol mientras lo tengas asignado'
      };
    }

    const usersWithRole = users?.filter(user => user.role === roleId) || [];
    
    return {
      canDelete: true,
      usersAffected: usersWithRole.length,
      usersList: usersWithRole
    };
  };

  const handleRoleDelete = async (roleId) => {
    const deleteCheck = canDeleteRole(roleId);
    
    if (!deleteCheck.canDelete) {
      showFeedback("error", deleteCheck.reason);
      return false;
    }

    try {
      const batch = writeBatch(db);
      
      if (deleteCheck.usersAffected > 0) {
        const confirmMessage = `Este rol está asignado a ${deleteCheck.usersAffected} usuario(s). ` +
          `Al eliminarlo, estos usuarios serán reasignados automáticamente al rol "Usuario". ` +
          `¿Desea continuar?`;
        
        if (!window.confirm(confirmMessage)) {
          return false;
        }

        deleteCheck.usersList.forEach(user => {
          const userRef = doc(db, 'users', user.id);
          batch.update(userRef, { 
            role: 'user',
            roleChangedAt: new Date().toISOString(),
            roleChangedReason: `Rol anterior "${roleId}" eliminado - reasignado automáticamente`
          });
        });

        showFeedback("info", `${deleteCheck.usersAffected} usuario(s) reasignado(s) al rol "Usuario"`);
      }

      const roleRef = doc(db, 'roles', roleId);
      batch.delete(roleRef);

      await batch.commit();
      
      showFeedback("success", `Rol "${roleId}" eliminado exitosamente`);
      onUpdate();
      return true;

    } catch (error) {
      console.error('Error deleting role:', error);
      showFeedback("error", "Error al eliminar rol: " + error.message);
      return false;
    }
  };

  // ✅ COMPONENTE CORREGIDO - Ahora recibe y pasa todos los props necesarios
  const RoleFormModal = ({ isEditing = false, role = null, onSuccess, onCancel }) => (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderInfo}>
            <h3 className={styles.modalTitle}>
              {isEditing ? (
                <>
                  <Shield size={24} />
                  Editar Rol: {role?.name}
                </>
              ) : (
                <>
                  <UserPlus size={24} />
                  Crear Nuevo Rol
                </>
              )}
            </h3>
            <p className={styles.modalSubtitle}>
              {isEditing 
                ? 'Modifica los datos del rol seleccionado'
                : 'Define un nuevo rol con permisos específicos para los usuarios'
              }
            </p>
          </div>
          <button
            className={styles.closeButton}
            onClick={onCancel}
            title="Cerrar"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className={styles.modalContent}>
          <CreateRoleForm 
            availablePermissions={availablePermissions}
            organizationalUnits={organizationalUnits}
            editingRole={role}
            onCancel={onCancel}
            onSuccess={onSuccess}
            showFeedback={showFeedback}
            isModal={true}
            currentUser={currentUser}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={styles.stats}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>{roles.length}</span>
              <span className={styles.statLabel}>Roles creados</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>{availablePermissions.length}</span>
              <span className={styles.statLabel}>Permisos disponibles</span>
            </div>
          </div>
        </div>
        
        <button 
          className={styles.createButton}
          onClick={handleCreateClick}
          title="Crear nuevo rol"
        >
          <Plus size={18} />
          <span className={styles.buttonText}>Crear Rol</span>
        </button>
      </div>

      <RoleList 
        roles={roles}
        availablePermissions={availablePermissions}
        onUpdate={onUpdate}
        showFeedback={showFeedback}
        onEditRole={handleEditRole}
        onDeleteRole={handleRoleDelete}
        canDeleteRole={canDeleteRole}
        currentUser={currentUser}
        organizationalUnits={organizationalUnits}
        users={users}
      />

      {showCreateModal && createPortal(
        <RoleFormModal 
          isEditing={false}
          onSuccess={handleCreateSuccess}
          onCancel={handleCreateCancel}
        />,
        document.body
      )}

      {editingRole && createPortal(
        <RoleFormModal 
          isEditing={true}
          role={editingRole}
          onSuccess={handleEditSuccess}
          onCancel={handleEditCancel}
        />,
        document.body
      )}
    </div>
  );
};

export default RoleManagement;